import React, { useState, useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { ArrowRight, Zap, Clock, MessageCircle, ShieldCheck, TrendingUp, Menu, X, Copy, Check, Mail, PlayCircle, Volume2, Loader2, Sparkles, HelpCircle, Play, Pause, Bell } from "lucide-react";
import { toast } from "sonner";
import AuthModal from "@/components/AuthModal";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/lib/i18n";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { fetchTTS, fetchPublicTTS, playBase64, stopAudio } from "@/lib/tts";

const GAIN_TIERS = [
  { g: 5, end: "16:20", saved: "40 min", work: 91.7 },
  { g: 10, end: "15:40", saved: "1h20", work: 83.3 },
  { g: 15, end: "15:00", saved: "2h00", work: 75 },
  { g: 20, end: "14:20", saved: "2h40", work: 66.7 },
];

function GainDemo() {
  const { t } = useI18n();
  const [i, setI] = useState(1);
  const [secs, setSecs] = useState(9 * 3600);
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.3 });
  useEffect(() => {
    if (!inView) return;
    const id = setInterval(() => setI((p) => (p + 1) % GAIN_TIERS.length), 4000);
    return () => clearInterval(id);
  }, [inView]);
  const tier = GAIN_TIERS[i];
  useEffect(() => {
    if (!inView) return;
    const mult = 1 + tier.g / 5;
    const id = setInterval(() => setSecs((s) => s + mult), 55);
    return () => clearInterval(id);
  }, [inView, tier.g]);
  const pad = (n) => String(Math.floor(n)).padStart(2, "0");
  const digital = `${pad((secs / 3600) % 24)}:${pad((secs / 60) % 60)}:${pad(secs % 60)}`;
  return (
    <section ref={ref} className="max-w-3xl mx-auto px-5 py-16" data-testid="demo-section">
      <div className="text-center mb-8">
        <h2 className="font-display text-3xl md:text-4xl font-extrabold mb-3">{t("demo_title")}</h2>
        <p className="text-white/50">{t("demo_sub")}</p>
      </div>
      <div className="gt-card p-6 md:p-8 space-y-7">
        <div className="flex justify-center">
          <div className="relative w-36 h-[184px] rounded-[34px] p-[3px] bg-gradient-to-b from-[#2a2a2a] to-[#0d0d0d] shadow-[0_0_34px_rgba(0,123,255,0.28)]" data-testid="analog-watch">
            <svg viewBox="0 0 152 192" className="absolute -inset-1 w-[calc(100%+8px)] h-[calc(100%+8px)] pointer-events-none z-20 overflow-visible" data-testid="watch-progress-ring">
              <defs>
                <linearGradient id="ringGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#007BFF" />
                  <stop offset="100%" stopColor="#00E676" />
                </linearGradient>
              </defs>
              <rect x="4" y="4" width="144" height="184" rx="38" ry="38" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="3" />
              <rect x="4" y="4" width="144" height="184" rx="38" ry="38" fill="none" stroke="url(#ringGrad)" strokeWidth="3" strokeLinecap="round"
                pathLength="100" strokeDasharray="100"
                strokeDashoffset={100 - (tier.g / 20) * 100}
                style={{ transition: "stroke-dashoffset 1.2s cubic-bezier(0.4,0,0.2,1)", filter: "drop-shadow(0 0 5px rgba(0,230,118,0.6))" }} />
            </svg>
            <div className="absolute -right-[3px] top-[52px] w-[5px] h-9 rounded-r-md bg-gradient-to-b from-[#007BFF] to-[#0056b3] z-30" />
            <div className="w-full h-full rounded-[31px] bg-[#080808] border border-white/5 flex flex-col items-center justify-center gap-1.5 overflow-hidden relative">
              <div className="absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_50%_25%,rgba(0,123,255,0.18),transparent_60%)]" />
              <span className="text-[9px] uppercase tracking-[0.2em] text-white/40 z-10">Gaim<span className="text-[#007BFF]">Time</span></span>
              <span className="font-mono-clock text-2xl font-bold text-[#00E676] z-10 tabular-nums drop-shadow-[0_0_8px_rgba(0,230,118,0.5)]" data-testid="watch-digital">{digital}</span>
              <span className="text-[10px] font-semibold text-[#007BFF] bg-[#007BFF]/10 rounded-full px-2 py-0.5 z-10">+{tier.g} min/h</span>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <motion.span key={tier.g} initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-1.5 text-sm font-bold text-[#00E676] bg-[#00E676]/10 rounded-full px-3 py-1" data-testid="demo-gain-badge">
            +{tier.g} min/h
          </motion.span>
          <div className="flex gap-1.5 items-center">
            {GAIN_TIERS.map((_, k) => (
              <button key={k} onClick={() => setI(k)} aria-label={`gain ${GAIN_TIERS[k].g}`}
                className={`h-1.5 rounded-full transition-all ${k === i ? "w-5 bg-[#00E676]" : "w-1.5 bg-white/20"}`} />
            ))}
          </div>
        </div>
        <div>
          <div className="flex justify-between items-center text-xs text-white/40 mb-2">
            <span>{t("demo_classic")}</span>
            <span className="font-mono-clock">09:00 → 17:00</span>
          </div>
          <div className="h-4 rounded-full bg-white/10" />
        </div>
        <div>
          <div className="flex justify-between items-center text-xs mb-2">
            <span className="text-[#007BFF] font-semibold">{t("demo_withgaim")}</span>
            <motion.span key={tier.end} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="font-mono-clock text-white/60" data-testid="demo-endtime">09:00 → {tier.end}</motion.span>
          </div>
          <div className="relative h-4 rounded-full bg-white/5 overflow-hidden">
            <motion.div animate={{ width: `${tier.work}%` }} transition={{ duration: 0.8, ease: "easeOut" }}
              className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-[#007BFF] to-[#00E676]" />
            <motion.div animate={{ width: `${(100 - tier.work).toFixed(1)}%` }} transition={{ duration: 0.8, ease: "easeOut" }}
              className="absolute inset-y-0 right-0 bg-[#00E676]/10 border-l border-dashed border-[#00E676]/50" />
          </div>
          <div className="flex justify-end mt-2">
            <motion.span key={tier.saved} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
              className="text-xs text-[#00E676] font-semibold" data-testid="demo-saved">↑ {t("demo_saved", { v: tier.saved })}</motion.span>
          </div>
        </div>
      </div>
    </section>
  );
}

import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";

function MiniClock({ label, value, accent, sub, testid }) {
  return (
    <div className="flex flex-col min-w-0">
      <span className="text-[10px] uppercase tracking-widest text-white/40 mb-1 truncate">{label}</span>
      <span data-testid={testid} className={`font-mono-clock text-xl sm:text-2xl md:text-3xl font-semibold whitespace-nowrap ${accent}`}>{value}</span>
      {sub && <span className="text-[10px] text-white/30 mt-0.5 font-mono-clock leading-tight">{sub}</span>}
    </div>
  );
}

function DashboardPreview() {
  const { t } = useI18n();
  const startRef = useRef(Date.now());
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    let raf;
    const loop = () => { setNow(Date.now()); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);
  const pad = (n) => String(Math.floor(n)).padStart(2, "0");
  const factor = 60 / 50; // gain 10 min/h -> 1h GaimTime = 50 min réelles (×1.2)
  const elapsed = now - startRef.current;
  const rd = new Date(now);
  const real = `${pad(rd.getHours())}:${pad(rd.getMinutes())}:${pad(rd.getSeconds())}`;
  const g = new Date(startRef.current + 47 * 60 * 1000 + elapsed * factor);
  const gaim = `${pad(g.getHours())}:${pad(g.getMinutes())}:${pad(g.getSeconds())}`;
  const gainSec = Math.floor((47 * 60 * 1000 + elapsed * (factor - 1)) / 1000);
  const gainStr = `${pad(gainSec / 3600)}:${pad((gainSec % 3600) / 60)}:${pad(gainSec % 60)}`;

  return (
    <div className="gt-card p-5 md:p-6 relative overflow-hidden">
      <div className="absolute -top-20 -right-20 w-52 h-52 rounded-full bg-[#007BFF]/10 blur-3xl" />
      <div className="flex items-center justify-between mb-5">
        <span className="text-xs text-white/40">{t("today_gain")}</span>
        <span className="flex items-center gap-1.5 text-[#00E676] text-xs">
          <span className="w-2 h-2 rounded-full bg-[#00E676] gt-live-dot" /> LIVE
        </span>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:gap-6 mb-3">
        <MiniClock label={t("real_clock")} value={real} accent="text-white" testid="preview-clock-real" />
        <MiniClock label={t("gaim_clock")} value={gaim} accent="text-[#007BFF] text-glow-blue" sub={t("conv", { n: 50 })} testid="preview-clock-gaim" />
      </div>
      <p data-testid="preview-explain" className="text-xs text-white/50 leading-relaxed mb-5">{t("preview_explain")}</p>
      <div className="flex items-end justify-between mb-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/40">{t("gain_cumule")}</div>
          <div data-testid="preview-gain" className="font-mono-clock text-2xl font-bold text-[#00E676] text-glow-mint">+ {gainStr}</div>
        </div>
        <TrendingUp className="text-[#00E676]" size={28} />
      </div>
      <div className="h-2 rounded-full bg-white/5 mt-3 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-[#007BFF] to-[#00E676] rounded-full" style={{ width: "62%" }} />
      </div>
    </div>
  );
}

export default function Landing() {
  const [modal, setModal] = useState({ open: false, tab: "register" });
  const [menu, setMenu] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [hiwOpen, setHiwOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState(false);
  const [audioState, setAudioState] = useState("idle"); // idle | loading | playing
  const CONTACT_EMAIL = "gaimtimeapp@gmail.com";
  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(CONTACT_EMAIL);
      setCopied(true);
      toast.success(t("copied"));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("—");
    }
  };
  const { user, ready } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  useEffect(() => {
    if (ready && user) {
      if (user.is_admin) navigate("/admin");
      else if (user.is_paid) navigate("/dashboard");
    }
  }, [ready, user, navigate]);

  const openReg = () => setModal({ open: true, tab: "register" });
  const openLogin = () => setModal({ open: true, tab: "login" });

  const hiwText = () =>
    [t("hiw_title"), t("hiw_intro"),
     `${t("hiw_s1_t")}. ${t("hiw_s1_d")}`,
     `${t("hiw_s2_t")}. ${t("hiw_s2_d")}`,
     `${t("hiw_s3_t")}. ${t("hiw_s3_d")}`,
     `${t("hiw_s4_t")}. ${t("hiw_s4_d")}`,
     t("hiw_example")].join(" ");

  const playHiw = async () => {
    if (audioState === "playing") { stopAudio(); setAudioState("idle"); return; }
    setAudioState("loading");
    try {
      const audio = await fetchPublicTTS(hiwText());
      playBase64(audio, () => setAudioState("idle"));
      setAudioState("playing");
    } catch {
      setAudioState("idle");
      toast.error(t("hiw_audio_error"));
    }
  };

  const openHiw = () => { setHiwOpen(true); playHiw(); };

  const closeHiw = () => { stopAudio(); setAudioState("idle"); setHiwOpen(false); };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white grain">
      {/* Nav */}
      <nav className="fixed top-0 inset-x-0 z-40 backdrop-blur-xl bg-[#0A0A0A]/70 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="font-display text-xl font-extrabold text-white">Gaim<span className="text-[#007BFF]">Time</span></div>
          <div className="hidden md:flex items-center gap-3">
            <LanguageSwitcher compact />
            <button data-testid="nav-login-btn" onClick={openLogin} className="text-sm text-white/70 hover:text-white transition-colors px-4 py-2">{t("nav_login")}</button>
            <Button data-testid="nav-cta-btn" onClick={openReg} className="bg-[#007BFF] hover:bg-[#0056b3] rounded-[12px] transition-all hover:scale-105">{t("nav_cta")}</Button>
          </div>
          <div className="md:hidden flex items-center gap-2">
            <LanguageSwitcher compact />
            <button className="text-white" onClick={() => setMenu(!menu)} data-testid="nav-menu-btn">{menu ? <X /> : <Menu />}</button>
          </div>
        </div>
        {menu && (
          <div className="md:hidden px-5 pb-4 flex flex-col gap-2 border-t border-white/5">
            <button onClick={openLogin} className="text-left py-2 text-white/70">{t("nav_login")}</button>
            <Button onClick={openReg} className="bg-[#007BFF] rounded-[12px]">{t("nav_cta")}</Button>
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="relative pt-28 md:pt-40 pb-16 px-5 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-[#007BFF]/10 blur-[120px]" />
        <div className="max-w-6xl mx-auto grid md:grid-cols-12 gap-10 md:gap-12 items-center relative">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="md:col-span-7 min-w-0">
            <div className="inline-flex items-center gap-2 text-xs text-[#00E676] border border-[#00E676]/30 rounded-full px-3 py-1 mb-6">
              <Zap size={13} /> {t("hero_badge")}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] break-words">
              {t("hero_t1")} <span className="text-[#007BFF] text-glow-blue">{t("hero_hl")}</span>
            </h1>
            <p className="text-base md:text-lg text-white/60 mt-6 max-w-xl leading-relaxed">
              {t("hero_sub")}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <button data-testid="hero-cta-btn" onClick={openReg} className="gt-pulse-blue w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-[12px] min-h-14 py-3 px-6 sm:px-8 text-sm sm:text-base font-semibold text-[#007BFF] bg-[#0A0A0A] border border-[#007BFF]/50 hover:bg-[#007BFF]/10 transition-all hover:scale-105">
                {t("hero_cta")} <span className="text-white/50 text-xs font-normal">{t("hero_cta_sub")}</span> <ArrowRight className="shrink-0" size={18} />
              </button>
              <button data-testid="hiw-open-btn" onClick={openHiw} className="gt-pulse-mint w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-[12px] min-h-14 py-3 px-6 sm:px-8 text-sm sm:text-base font-semibold text-[#00E676] bg-[#0A0A0A] border border-[#00E676]/50 hover:bg-[#00E676]/10 transition-all hover:scale-105">
                <PlayCircle size={20} /> {t("hiw_open")} <span className="text-white/50 text-xs font-normal">· 30s</span>
              </button>
            </div>
            <div className="mt-4 flex flex-col items-start gap-3">
              <button data-testid="hero-faq-btn" onClick={() => setFaqOpen(true)} className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white border border-white/15 hover:border-[#00E676]/50 rounded-full px-4 py-2 transition-colors">
                <HelpCircle size={16} className="text-[#00E676]" /> {t("faq_title")}
              </button>
            </div>
            <div className="flex items-center gap-2 mt-4 text-xs text-white/40 flex-wrap">
              <ShieldCheck size={14} className="text-[#00E676]" /> {t("hero_trust")}
              <span className="inline-flex items-center rounded-[4px] bg-[#635BFF] text-white font-bold px-1.5 py-0.5 text-[10px] tracking-tight" data-testid="stripe-badge">stripe</span>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.15 }} className="md:col-span-5 min-w-0 w-full">
            <DashboardPreview />
          </motion.div>
        </div>
      </section>

      {/* Concept — visual demo */}
      <GainDemo />

      {/* Companion */}
      <section className="max-w-6xl mx-auto px-5 py-16">
        <div className="gt-card p-8 md:p-12 relative overflow-hidden">
          <div className="absolute -bottom-24 -left-24 w-64 h-64 rounded-full bg-[#00E676]/10 blur-3xl" />
          <div className="grid md:grid-cols-2 gap-8 items-center relative">
            <div>
              <div className="inline-flex items-center gap-2 text-[#00E676] text-sm mb-4"><MessageCircle size={18} /> {t("comp_badge")}</div>
              <h2 className="font-display text-3xl md:text-4xl font-extrabold mb-4">{t("comp_t")}</h2>
              <p className="text-white/60 leading-relaxed mb-6">
                {t("comp_d")}
              </p>
              <div className="text-sm text-white/40">{t("comp_avail")}</div>
            </div>
            <div className="bg-[#0A0A0A] rounded-[16px] border border-white/5 p-5 space-y-3">
              <div className="flex justify-end"><div className="bg-[#007BFF] rounded-[12px] rounded-tr-sm px-4 py-2 text-sm max-w-[80%]">{t("chat_demo_q")}</div></div>
              <div className="flex justify-start"><div className="bg-[#1A1A1A] rounded-[12px] rounded-tl-sm px-4 py-2 text-sm max-w-[85%] text-white/80">{t("chat_demo_a")}</div></div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="max-w-5xl mx-auto px-5 py-16">
        <div className="text-center mb-10">
          <h2 className="font-display text-3xl md:text-4xl font-extrabold mb-3">{t("offer_clear_t")}</h2>
          <p className="text-white/50 max-w-2xl mx-auto">{t("offer_clear_sub")}</p>
        </div>
        <div className="grid md:grid-cols-2 gap-5 items-stretch">
          {/* Card 1 — Lifetime */}
          <div className="gt-card p-8 flex flex-col gt-glow-blue" data-testid="offer-card-lifetime">
            <div className="text-xs uppercase tracking-widest text-[#007BFF] mb-3">{t("card1_tag")}</div>
            <div className="font-display text-xl font-extrabold mb-2">Gaim<span className="text-[#007BFF]">Time</span></div>
            <div className="flex items-baseline gap-2 mb-5">
              <span className="font-mono-clock text-5xl font-bold text-white">4,99 €</span>
              <span className="text-white/40 text-sm">{t("price_life")}</span>
            </div>
            <ul className="space-y-3 mb-6 text-sm text-white/70 flex-1">
              {[t("pf1"), t("pf2"), t("pf3"), t("pf4")].map((x, i) => (
                <li key={i} className="flex items-center gap-2"><span className="text-[#00E676]">✓</span> {x}</li>
              ))}
            </ul>
            <Button data-testid="pricing-cta-btn" onClick={openReg} className="w-full bg-[#007BFF] hover:bg-[#0056b3] rounded-[12px] h-12 font-semibold transition-all hover:scale-[1.02]">{t("nav_cta")}</Button>
          </div>
          {/* Card 2 — Coach (option) */}
          <div className="gt-card p-8 flex flex-col border-dashed border-white/15" data-testid="offer-card-coach">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#00E676] mb-3">
              <Sparkles size={13} /> {t("card2_tag")} <span className="text-white/30 normal-case tracking-normal">{t("card2_optional")}</span>
            </div>
            <div className="font-display text-xl font-extrabold mb-2">{t("card2_title")}</div>
            <div className="flex items-baseline gap-2 mb-5">
              <span className="font-mono-clock text-5xl font-bold text-[#00E676]">{t("card2_price")}</span>
              <span className="text-white/40 text-sm">{t("permonth")}</span>
            </div>
            <ul className="space-y-3 mb-6 text-sm text-white/70 flex-1">
              <li className="flex items-center gap-2"><span className="text-[#00E676]">✓</span> {t("card2_feat")}</li>
              <li className="flex items-center gap-2"><span className="text-[#00E676]">✓</span> {t("card2_feat2")}</li>
            </ul>
            <div className="text-xs text-white/40 text-center py-3 border border-white/10 rounded-[12px]" data-testid="coach-availability-note">{t("card2_note")}</div>
          </div>
        </div>
        <p className="text-center text-sm text-white/40 mt-8 max-w-xl mx-auto flex items-center justify-center gap-2" data-testid="pricing-reassure">
          <ShieldCheck size={16} className="text-[#00E676] shrink-0" /> {t("pricing_reassure")}
        </p>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 mt-10">
        <div className="max-w-6xl mx-auto px-5 py-10 flex flex-col md:flex-row justify-between gap-6 text-sm text-white/40">
          <div className="font-display text-lg font-extrabold text-white">Gaim<span className="text-[#007BFF]">Time</span></div>
          <div className="flex gap-6">
            <button onClick={() => setContactOpen(true)} className="hover:text-white transition-colors" data-testid="footer-contact">{t("footer_contact")}</button>
            <Link to="/cgv" className="hover:text-white transition-colors" data-testid="footer-cgv">{t("footer_cgv")}</Link>
            <Link to="/mentions-legales" className="hover:text-white transition-colors" data-testid="footer-legal">{t("footer_legal")}</Link>
          </div>
        </div>
      </footer>

      <AuthModal open={modal.open} initialTab={modal.tab} onClose={() => setModal({ ...modal, open: false })} />

      <Dialog open={hiwOpen} onOpenChange={(o) => !o && closeHiw()}>
        <DialogContent className="bg-[#111111] border border-white/10 text-white rounded-[16px] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-extrabold flex items-center gap-2">
              <Clock className="text-[#007BFF]" size={22} /> {t("hiw_title")}
            </DialogTitle>
          </DialogHeader>

          <button data-testid="hiw-play-btn" onClick={playHiw} disabled={audioState === "loading"}
            className="w-full flex items-center gap-3 rounded-[12px] border border-[#00E676]/40 bg-[#00E676]/10 hover:bg-[#00E676]/20 px-4 py-3 transition-colors disabled:opacity-60">
            <span className="shrink-0 w-11 h-11 rounded-full bg-[#00E676] text-black flex items-center justify-center">
              {audioState === "loading" ? <Loader2 className="animate-spin" size={22} /> : audioState === "playing" ? <Pause size={22} /> : <Play size={22} className="ml-0.5" />}
            </span>
            {audioState === "playing" ? (
              <span className="gt-eq flex items-end gap-[3px] h-5" aria-hidden="true">
                <span style={{ animationDelay: "0ms" }} /><span style={{ animationDelay: "150ms" }} /><span style={{ animationDelay: "300ms" }} /><span style={{ animationDelay: "450ms" }} /><span style={{ animationDelay: "200ms" }} />
              </span>
            ) : (
              <Volume2 size={18} className="text-[#00E676]" />
            )}
            <span className="text-sm font-semibold text-[#00E676] ml-auto" data-testid="hiw-audio-status">
              {audioState === "loading" ? t("hiw_loading") : audioState === "playing" ? t("hiw_playing") : t("hiw_listen")}
            </span>
          </button>

          <p className="text-white/70 text-sm leading-relaxed">{t("hiw_intro")}</p>
          <div className="space-y-3 mt-1">
            {[[Zap, "hiw_s1_t", "hiw_s1_d"], [Clock, "hiw_s2_t", "hiw_s2_d"], [TrendingUp, "hiw_s3_t", "hiw_s3_d"], [Bell, "hiw_s4_t", "hiw_s4_d"]].map(([Icon, tk, dk], i) => (
              <div key={i} className="flex gap-3 items-start">
                <div className="shrink-0 w-8 h-8 rounded-full bg-[#007BFF]/15 flex items-center justify-center text-[#007BFF]"><Icon size={16} /></div>
                <div>
                  <div className="font-semibold text-sm">{t(tk)}</div>
                  <div className="text-white/50 text-sm">{t(dk)}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="bg-[#0A0A0A] border border-[#00E676]/20 rounded-[12px] p-3 text-sm text-white/70 mt-1">{t("hiw_example")}</div>
          <Button data-testid="hiw-cta-btn" onClick={() => { closeHiw(); openReg(); }} className="w-full bg-[#007BFF] hover:bg-[#0056b3] rounded-[12px] h-12 font-semibold mt-1">
            {t("hero_cta")}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={faqOpen} onOpenChange={setFaqOpen}>
        <DialogContent className="bg-[#111111] border border-white/10 text-white rounded-[16px] max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-extrabold">{t("faq_title")}</DialogTitle>
          </DialogHeader>
          <Accordion type="single" collapsible className="w-full">
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <AccordionItem key={n} value={`faq-${n}`} className="border-white/10">
                <AccordionTrigger data-testid={`faq-q-${n}`} className="text-left text-sm font-medium hover:no-underline hover:text-[#007BFF]">
                  {t(`faq_q${n}`)}
                </AccordionTrigger>
                <AccordionContent className="text-white/60 text-sm leading-relaxed">
                  {t(`faq_a${n}`)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </DialogContent>
      </Dialog>

      <Dialog open={contactOpen} onOpenChange={setContactOpen}>
        <DialogContent className="bg-[#111111] border border-white/10 text-white rounded-[16px] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-extrabold flex items-center gap-2">
              <Mail className="text-[#007BFF]" size={22} /> {t("contact_title")}
            </DialogTitle>
          </DialogHeader>
          <p className="text-white/60 text-sm">{t("contact_desc")}</p>
          <div className="flex items-center gap-2 bg-[#0A0A0A] border border-white/10 rounded-[12px] p-3 mt-1">
            <span className="font-mono-clock text-[#00E676] text-sm flex-1 break-all" data-testid="contact-email">{CONTACT_EMAIL}</span>
            <Button data-testid="contact-copy-btn" onClick={copyEmail} className="bg-[#007BFF] hover:bg-[#0056b3] rounded-[10px] h-9 px-3">
              {copied ? <Check size={16} /> : <Copy size={16} />}
            </Button>
          </div>
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-xs text-white/40 hover:text-white transition-colors mt-1 inline-block" data-testid="contact-mailto">
            {t("contact_copy_or")}
          </a>
        </DialogContent>
      </Dialog>
    </div>
  );
}

